import * as React from "react";

import { ContentBatchRow } from "../../../common/contentTypes";
import { EffectiveGenerationStatus } from "../batchStatus";

type StepState = "pending" | "active" | "done" | "failed";

const ICON: Record<StepState, string> = {
  pending: "far fa-circle text-muted",
  active: "fas fa-circle-notch fa-spin text-primary",
  done: "fas fa-check-circle text-success",
  failed: "fas fa-times-circle text-danger",
};

/**
 * The three steps the designs show, driven by the batch document rather than a
 * timer — so a run that stalls or fails stops mid-list instead of animating to
 * a completion that never happened.
 *
 * "Running QA pass" is not a separate server phase: the rubric runs inside each
 * chunk. It is shown as in-flight while chunks are still landing and complete
 * when the run reaches a terminal state, which is exactly when every question
 * has been through QA.
 */
export const GenerationProgress: React.FC<{
  batch: ContentBatchRow;
  status: EffectiveGenerationStatus;
}> = ({ batch, status }) => {
  const requested = batch.generation?.requested ?? 0;
  const produced = batch.generation?.produced ?? 0;
  const broken = status === "failed" || status === "stalled";

  const generating: StepState =
    status === "done" || produced >= requested
      ? "done"
      : broken
      ? "failed"
      : "active";

  const qa: StepState =
    status === "done"
      ? "done"
      : broken
      ? "failed"
      : produced > 0
      ? "active"
      : "pending";

  const finished: StepState =
    status === "done" ? "done" : broken ? "failed" : "pending";

  const steps: [StepState, string][] = [
    [generating, `Generating ${requested} questions…`],
    [qa, "Running QA pass…"],
    [finished, "Done"],
  ];

  return (
    <div className="bg-light border rounded p-3 mt-3">
      {steps.map(([state, label]) => (
        <div className="d-flex align-items-center mb-2" key={label}>
          <i className={`${ICON[state]} mr-2`} style={{ width: 18 }} />
          <span
            className={
              state === "active"
                ? "font-weight-bold"
                : state === "pending"
                ? "text-muted"
                : ""
            }
          >
            {label}
          </span>
          {state === "active" && label.startsWith("Generating") && (
            <span className="text-muted small ml-2">
              {produced}/{requested}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};
